import { useId, useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import type {
  StorefrontTeam,
  StorefrontTeamMember,
  VisitFormat,
} from '@/features/storefront/api/team';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import type {
  TeamCardStyle,
  TeamFilterKind,
  TeamSection,
} from '@/features/storefront/sections/schema';
import {
  NO_TEAM_FILTER,
  NO_TEAM_MULTI_FILTER,
  bookHref,
  filterTeam,
  filterTeamMulti,
  formatAvailableDate,
  initials,
  memberPhoto,
  teamAvailability,
  teamMultiFilterActive,
  teamMultiFilterCount,
  type TeamMultiFilterState,
  shownMembers,
  teamFilterActive,
  teamFilterHasOptions,
  teamFilterKinds,
  teamFilterOptions,
  teamMemberHref,
  type TeamFilterOptions,
  type TeamFilterState,
} from '@/features/storefront/sections/team';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * The people of a service business, as a grid of cards.
 *
 * The people are the merchant's rows (`/storefront/team-members`), never the
 * section's - so the live shop draws nothing while there is nobody, and the
 * customiser says where to add them instead. Each card's name opens the
 * person's own page at `/team/{slug}`, and its button goes to the booking page
 * with `?member={slug}`, which the bookings section reads to start the shopper
 * on that person.
 *
 * Three card styles over one set of facts: `portrait` (a tall photo with the
 * words under it), `round` (a circular photo, centred, for a smaller team that
 * reads as people rather than as a catalogue) and `overlay` (the name and role
 * laid over the bottom of the photo, for a team with strong pictures).
 */

/** How a link is drawn: the renderer's own, so preview links stay inert. */
export type TeamLink = (
  href: string,
  className: string,
  children: ReactNode,
  style?: CSSProperties,
) => ReactNode;

export interface TeamViewProps {
  section: TeamSection;
  /** `null` while the list is still on its way. */
  team: StorefrontTeam | null;
  /** The customiser: says why the section is empty instead of vanishing. */
  preview: boolean;
  heading: ReactNode;
  /** What the customiser shows while the shop has nobody visible. */
  emptyHint: ReactNode;
  link: TeamLink;
}

const MUTED: CSSProperties = { color: 'var(--storefront-muted)' };

/**
 * The card's secondary button: the book button's border and corners with no
 * fill, so it reads as the lesser of the two on every theme - the text is the
 * primary colour on the page's own background, never on a fill this component
 * cannot vouch for.
 */
const OUTLINE_BUTTON_STYLE: CSSProperties = {
  backgroundColor: 'transparent',
  color: 'var(--storefront-primary)',
  border: '1px solid var(--storefront-primary)',
  borderRadius: 'var(--storefront-radius)',
};

/**
 * A card photo: one column on a phone, two from `sm`, up to three of the page
 * at `lg` - and a third of the wider page a big screen gets (`gridImageSizes`).
 */
const TEAM_PHOTO_SIZES =
  '(min-width: 1440px) calc(92vw / 3), (min-width: 1024px) 448px, (min-width: 640px) 50vw, 100vw';

/** Column counts, pre-resolved so Tailwind can see them. Phones always get one. */
const GRID: Record<number, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
};

/**
 * The person's photo in its frame - or a sample person's stand-in silhouette
 * (`memberPhoto`) - else their initials on the surface colour.
 */
function Portrait({
  member,
  roster,
  shape,
  className,
}: {
  member: StorefrontTeamMember;
  /** Everyone in the shop's order, which seeds a sample person's stand-in. */
  roster: readonly StorefrontTeamMember[];
  shape: TeamCardStyle;
  className?: string;
}) {
  const photo = memberPhoto(member, roster);
  const round = shape === 'round';
  const frame = cn(
    'block overflow-hidden',
    round ? 'mx-auto aspect-square w-32 rounded-full sm:w-36' : 'aspect-[4/5] w-full',
    className,
  );
  const frameStyle: CSSProperties = round
    ? { backgroundColor: 'var(--storefront-surface)' }
    : { backgroundColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' };

  return (
    <span className={frame} style={frameStyle} data-team-photo>
      {photo ? (
        <StorefrontImage
          url={photo}
          sizes={round ? fixedImageSizes(144) : TEAM_PHOTO_SIZES}
          alt=""
          className="size-full object-cover"
          style={focalStyle(member.photo_focal)}
        />
      ) : (
        <span
          aria-hidden
          className="flex size-full items-center justify-center text-2xl font-semibold"
          style={MUTED}
        >
          {initials(member.name)}
        </span>
      )}
    </span>
  );
}

function MemberCard({
  section,
  member,
  roster,
  link,
}: {
  section: TeamSection;
  member: StorefrontTeamMember;
  roster: readonly StorefrontTeamMember[];
  link: TeamLink;
}) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();
  const style = section.card_style;
  const profile = teamMemberHref(member.slug);
  const centred = style === 'round';

  const name = link(
    profile,
    'hover:underline',
    <>{member.name}</>,
  );

  const booking = section.show_book_button && section.book_label.trim();
  const profileLabel = copy('team.view_profile').trim();
  const profileButton = section.show_profile_button && profileLabel;

  // Without the profile button the book button is drawn exactly as it always
  // was - its own `mt-4`, no wrapper - so a section that never turns the new
  // switch on keeps its cards to the pixel. With it, the two sit in one
  // wrapping row: Book first (the card's main action), then the outline.
  let button: ReactNode = null;
  if (!profileButton) {
    button = booking
      ? link(
          bookHref(section.book_href, member.slug),
          cn(
            'mt-4 inline-flex h-10 items-center px-5 text-sm font-semibold',
            centred && 'self-center',
          ),
          <>{section.book_label}</>,
          STOREFRONT_BUTTON_STYLE,
        )
      : null;
  } else {
    button = (
      <div
        className={cn('mt-4 flex flex-wrap gap-2', centred && 'justify-center self-center')}
        data-team-actions
      >
        {booking
          ? link(
              bookHref(section.book_href, member.slug),
              'inline-flex h-10 items-center px-5 text-sm font-semibold',
              <>{section.book_label}</>,
              STOREFRONT_BUTTON_STYLE,
            )
          : null}
        {link(
          profile,
          'inline-flex h-10 items-center px-5 text-sm font-semibold',
          <span data-team-profile-button>{profileLabel}</span>,
          OUTLINE_BUTTON_STYLE,
        )}
      </div>
    );
  }

  const availability = section.show_availability ? teamAvailability(member) : null;
  const available = availability ? (
    <p
      className={cn(
        'mt-2 inline-flex items-center gap-1.5 text-xs font-semibold',
        centred && 'self-center',
      )}
      style={{ color: availability.kind === 'today' ? 'var(--storefront-primary)' : 'var(--storefront-muted)' }}
      data-team-availability={availability.kind}
    >
      {/* A dot, so "free today" reads at a glance without leaning on colour alone. */}
      <span aria-hidden className="size-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
      {availability.kind === 'today'
        ? copy('team.available_today')
        : copy('team.next_available', { date: formatAvailableDate(availability.date, locale) })}
    </p>
  ) : null;

  const bio =
    section.show_bio && member.short_bio ? (
      <p className="mt-2 text-sm leading-relaxed" style={MUTED} data-team-bio>
        {member.short_bio}
      </p>
    ) : null;

  if (style === 'overlay') {
    return (
      <li className="flex flex-col" data-team-member={member.slug} data-card-style="overlay">
        <div className="relative">
          <Portrait member={member} roster={roster} shape={style} />
          <div
            className="absolute inset-x-0 bottom-0 p-4 text-white"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0))',
              borderBottomLeftRadius: 'var(--storefront-radius)',
              borderBottomRightRadius: 'var(--storefront-radius)',
            }}
          >
            <h3 className="text-lg font-semibold" style={STOREFRONT_HEADING_STYLE}>
              {name}
            </h3>
            {member.role ? <p className="text-sm opacity-90">{member.role}</p> : null}
          </div>
        </div>
        {available}
        {bio}
        {button}
      </li>
    );
  }

  return (
    <li
      className={cn('flex flex-col', centred && 'items-center text-center')}
      data-team-member={member.slug}
      data-card-style={style}
    >
      {/* The name is the link, not the photo: a link whose only content is a
          decorative picture is one a screen reader cannot name. */}
      <Portrait member={member} roster={roster} shape={style} />

      <h3 className="mt-4 text-lg font-semibold" style={STOREFRONT_HEADING_STYLE}>
        {name}
      </h3>
      {member.role ? (
        <p className="text-sm" style={MUTED}>
          {member.role}
        </p>
      ) : null}
      {available}
      {bio}
      {button}
    </li>
  );
}

/**
 * One filter choice: a pill that says whether it is on.
 *
 * The same "chosen" mark as the booking calendar's day strip: the theme's
 * primary as an outline, never a fill whose text colour this component cannot
 * guarantee against it.
 */
export function TeamChip({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn('rounded-full border px-4 py-1.5 text-sm', pressed && 'font-semibold')}
      style={{
        borderColor: pressed ? 'var(--storefront-primary)' : 'var(--storefront-surface)',
        color: pressed ? 'var(--storefront-primary)' : 'var(--storefront-text)',
      }}
    >
      {children}
    </button>
  );
}

/** "Monday", in the shopper's language - a select has room for the whole word. */
function weekdayLong(weekday: number, locale: string): string {
  // 2024-01-01 was a Monday; noon UTC keeps every zone on the same date.
  const date = new Date(Date.UTC(2024, 0, 1 + weekday, 12));
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', { weekday: 'long', timeZone: 'UTC' }).format(date);
  }
}

const FIELD_STYLE: CSSProperties = {
  borderColor: 'var(--storefront-surface)',
  borderRadius: 'var(--storefront-radius)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-text)',
};

const VISIT_KEYS: Record<VisitFormat, string> = {
  in_person: 'team.visit_in_person',
  online: 'team.visit_online',
  home: 'team.visit_home',
};

const FILTER_LABEL_KEYS: Record<TeamFilterKind, string> = {
  speciality: 'team.filter_speciality',
  availability: 'team.filter_availability',
  visit_format: 'team.filter_visit_format',
  experience: 'team.filter_experience',
  gender: 'team.filter_gender',
  language: 'team.filter_language',
};

/**
 * A choice the shopper made that the people no longer offer - a chip whose
 * person has since gone, a filter the merchant switched off - reads as unset,
 * so the grid never stays narrowed by a button that is not on screen.
 */
function effectiveState(
  state: TeamFilterState,
  kinds: readonly TeamFilterKind[],
  options: TeamFilterOptions,
  searchable: boolean,
): TeamFilterState {
  const on = (kind: TeamFilterKind) => kinds.includes(kind);
  return {
    speciality:
      on('speciality') && state.speciality && options.speciality.includes(state.speciality)
        ? state.speciality
        : null,
    when: on('availability') && state.when && options.when.includes(state.when) ? state.when : null,
    weekday:
      on('availability') && state.weekday !== null && options.weekday.includes(state.weekday)
        ? state.weekday
        : null,
    visit_format:
      on('visit_format') && state.visit_format && options.visit_format.includes(state.visit_format)
        ? state.visit_format
        : null,
    experience:
      on('experience') && state.experience !== null && options.experience.includes(state.experience)
        ? state.experience
        : null,
    gender: on('gender') && state.gender && options.gender.includes(state.gender) ? state.gender : null,
    language:
      on('language') &&
      state.language &&
      options.language.some((language) => language.toLowerCase() === state.language)
        ? state.language
        : null,
    search: searchable ? state.search : '',
  };
}

/** How many filters are set - the count on the phone's "Filters" button. */
function setCount(state: TeamFilterState): number {
  return [
    state.speciality,
    state.when,
    state.weekday,
    state.visit_format,
    state.experience,
    state.gender,
    state.language,
  ].filter((value) => value !== null).length;
}

/**
 * The search box and the filters over the grid.
 *
 * Each filter is a labelled group of pressable chips (`aria-pressed`), the
 * weekday a labelled select. On a phone the filters fold behind one "Filters"
 * button so a clinic with six of them does not push the people a screen down;
 * from `sm` up they are always open and simply wrap.
 */
function TeamToolbar({
  kinds,
  options,
  searchable,
  state,
  onChange,
  count,
  total,
}: {
  kinds: readonly TeamFilterKind[];
  options: TeamFilterOptions;
  searchable: boolean;
  state: TeamFilterState;
  onChange: (next: TeamFilterState) => void;
  count: number;
  total: number;
}) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();
  const [open, setOpen] = useState(false);
  const id = useId();
  const set = <K extends keyof TeamFilterState>(key: K, value: TeamFilterState[K]) =>
    onChange({ ...state, [key]: value });
  // A pressed chip pressed again lets go, so every chip is its own "any".
  const toggle = <K extends keyof TeamFilterState>(key: K, value: TeamFilterState[K]) =>
    set(key, state[key] === value ? (null as TeamFilterState[K]) : value);
  const filtersSet = setCount(state);

  const group = (kind: TeamFilterKind, body: ReactNode) => (
    <div
      key={kind}
      role="group"
      aria-labelledby={`${id}-${kind}`}
      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
      data-team-filter={kind}
    >
      <span
        id={`${id}-${kind}`}
        className="shrink-0 text-xs font-semibold uppercase tracking-wider"
        style={MUTED}
      >
        {copy(FILTER_LABEL_KEYS[kind])}
      </span>
      <div className="flex flex-wrap items-center gap-2">{body}</div>
    </div>
  );

  const groups = kinds.map((kind) => {
    switch (kind) {
      case 'speciality':
        return group(
          kind,
          [null, ...options.speciality].map((value) => (
            <TeamChip
              key={value ?? '__all'}
              pressed={state.speciality === value}
              onClick={() => set('speciality', value)}
            >
              {value ?? copy('team.filter_all')}
            </TeamChip>
          )),
        );
      case 'availability':
        return group(
          kind,
          <>
            {options.when.map((when) => (
              <TeamChip key={when} pressed={state.when === when} onClick={() => toggle('when', when)}>
                {copy(when === 'today' ? 'team.available_today' : 'team.available_week')}
              </TeamChip>
            ))}
            {options.weekday.length > 0 ? (
              <label className="flex items-center gap-2 text-sm">
                <span className="sr-only">{copy('team.weekday_label')}</span>
                <select
                  value={state.weekday === null ? '' : String(state.weekday)}
                  onChange={(event) =>
                    set('weekday', event.target.value === '' ? null : Number(event.target.value))
                  }
                  className="h-9 border px-3 text-sm"
                  style={FIELD_STYLE}
                  data-team-weekday
                >
                  <option value="">{copy('team.weekday_any')}</option>
                  {options.weekday.map((weekday) => (
                    <option key={weekday} value={weekday}>
                      {weekdayLong(weekday, locale)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>,
        );
      case 'visit_format':
        return group(
          kind,
          options.visit_format.map((format) => (
            <TeamChip
              key={format}
              pressed={state.visit_format === format}
              onClick={() => toggle('visit_format', format)}
            >
              {copy(VISIT_KEYS[format])}
            </TeamChip>
          )),
        );
      case 'experience':
        return group(
          kind,
          options.experience.map((years) => (
            <TeamChip
              key={years}
              pressed={state.experience === years}
              onClick={() => toggle('experience', years)}
            >
              {copy('team.experience_min', { years })}
            </TeamChip>
          )),
        );
      case 'gender':
        return group(
          kind,
          options.gender.map((gender) => (
            <TeamChip key={gender} pressed={state.gender === gender} onClick={() => toggle('gender', gender)}>
              {copy(gender === 'female' ? 'team.gender_female' : 'team.gender_male')}
            </TeamChip>
          )),
        );
      case 'language':
        return group(
          kind,
          options.language.map((language) => (
            <TeamChip
              key={language}
              pressed={state.language === language.toLowerCase()}
              onClick={() => toggle('language', language.toLowerCase())}
            >
              {language}
            </TeamChip>
          )),
        );
    }
  });

  return (
    <div className="mb-8 flex flex-col gap-4" data-team-toolbar>
      {searchable ? (
        <input
          type="search"
          value={state.search}
          onChange={(event) => set('search', event.target.value)}
          aria-label={copy('team.search_label')}
          placeholder={copy('team.search_label')}
          className="h-11 w-full border px-4 text-sm sm:max-w-sm"
          style={FIELD_STYLE}
          data-team-search
        />
      ) : null}
      {kinds.length > 0 ? (
        <>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-10 items-center self-start border px-4 text-sm font-semibold sm:hidden"
            style={FIELD_STYLE}
            data-team-filters-toggle
          >
            {copy('team.filters_toggle')}
            {filtersSet > 0 ? ` (${filtersSet})` : ''}
          </button>
          <div
            id={`${id}-panel`}
            className={cn('flex-col gap-4 sm:flex', open ? 'flex' : 'hidden')}
            data-team-filters
          >
            {groups}
          </div>
        </>
      ) : null}
      {teamFilterActive(state) ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <p aria-live="polite" style={MUTED} data-team-results>
            {copy('team.results', { count, total })}
          </p>
          <button
            type="button"
            onClick={() => onChange(NO_TEAM_FILTER)}
            className="font-semibold underline"
            data-team-clear
          >
            {copy('team.clear_filters')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// --- The sidebar layout -------------------------------------------------------
/**
 * The sidebar's version of `effectiveState`: a tick whose option the people
 * no longer offer, or in a group the merchant switched off, is dropped, so the
 * grid never stays narrowed by a box that is not on screen.
 */
function effectiveMultiState(
  state: TeamMultiFilterState,
  kinds: readonly TeamFilterKind[],
  options: TeamFilterOptions,
  searchable: boolean,
): TeamMultiFilterState {
  const on = (kind: TeamFilterKind) => kinds.includes(kind);
  const keep = <T,>(kind: TeamFilterKind, picked: readonly T[], offered: readonly T[]) =>
    on(kind) ? picked.filter((value) => offered.includes(value)) : [];
  const languages = options.language.map((language) => language.toLowerCase());
  return {
    speciality: keep('speciality', state.speciality, options.speciality),
    when: keep('availability', state.when, options.when),
    weekday:
      on('availability') && state.weekday !== null && options.weekday.includes(state.weekday)
        ? state.weekday
        : null,
    visit_format: keep('visit_format', state.visit_format, options.visit_format),
    experience: keep('experience', state.experience, options.experience),
    gender: keep('gender', state.gender, options.gender),
    language: keep('language', state.language, languages),
    search: searchable ? state.search : '',
  };
}

/** A list with `value` added, or taken out when it was there - one checkbox's click. */
function flip<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

/** One labelled checkbox: the whole row is the label, so the text is a target too. */
function TeamCheckbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 py-1 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-4 shrink-0"
        // The native box, tinted with the theme - it keeps its own contrast
        // and its keyboard and screen-reader behaviour for free.
        style={{ accentColor: 'var(--storefront-primary)' }}
      />
      <span className={cn(checked && 'font-semibold')}>{children}</span>
    </label>
  );
}

/**
 * The search box and the filters as a column beside the grid.
 *
 * Each filter is a `<fieldset>` with its `<legend>` and a real checkbox per
 * option, so a screen reader announces "Speciality, group" and then each box
 * with its state; ticks within one group widen it and groups narrow each other
 * (`filterTeamMulti`). The weekday stays a select inside the availability
 * group. Below `lg` the column folds behind the same "Filters (n)" button the
 * top bar uses on phones, and the grid takes the full width.
 */
function TeamSidebar({
  kinds,
  options,
  searchable,
  state,
  onChange,
  count,
  total,
}: {
  kinds: readonly TeamFilterKind[];
  options: TeamFilterOptions;
  searchable: boolean;
  state: TeamMultiFilterState;
  onChange: (next: TeamMultiFilterState) => void;
  count: number;
  total: number;
}) {
  const copy = useShopContent();
  const { locale } = useStorefrontLanguage();
  const [open, setOpen] = useState(false);
  const id = useId();
  const set = <K extends keyof TeamMultiFilterState>(key: K, value: TeamMultiFilterState[K]) =>
    onChange({ ...state, [key]: value });
  const ticked = teamMultiFilterCount(state);

  const group = (kind: TeamFilterKind, body: ReactNode) => (
    <fieldset key={kind} className="min-w-0" data-team-filter={kind}>
      <legend className="mb-2 text-xs font-semibold uppercase tracking-wider" style={MUTED}>
        {copy(FILTER_LABEL_KEYS[kind])}
      </legend>
      <div className="flex flex-col">{body}</div>
    </fieldset>
  );

  const groups = kinds.map((kind) => {
    switch (kind) {
      case 'speciality':
        return group(
          kind,
          options.speciality.map((value) => (
            <TeamCheckbox
              key={value}
              checked={state.speciality.includes(value)}
              onChange={() => set('speciality', flip(state.speciality, value))}
            >
              {value}
            </TeamCheckbox>
          )),
        );
      case 'availability':
        return group(
          kind,
          <>
            {options.when.map((when) => (
              <TeamCheckbox
                key={when}
                checked={state.when.includes(when)}
                onChange={() => set('when', flip(state.when, when))}
              >
                {copy(when === 'today' ? 'team.available_today' : 'team.available_week')}
              </TeamCheckbox>
            ))}
            {options.weekday.length > 0 ? (
              <label className="mt-2 flex flex-col gap-1 text-sm">
                <span className="sr-only">{copy('team.weekday_label')}</span>
                <select
                  value={state.weekday === null ? '' : String(state.weekday)}
                  onChange={(event) =>
                    set('weekday', event.target.value === '' ? null : Number(event.target.value))
                  }
                  className="h-9 w-full border px-3 text-sm"
                  style={FIELD_STYLE}
                  data-team-weekday
                >
                  <option value="">{copy('team.weekday_any')}</option>
                  {options.weekday.map((weekday) => (
                    <option key={weekday} value={weekday}>
                      {weekdayLong(weekday, locale)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>,
        );
      case 'visit_format':
        return group(
          kind,
          options.visit_format.map((format) => (
            <TeamCheckbox
              key={format}
              checked={state.visit_format.includes(format)}
              onChange={() => set('visit_format', flip(state.visit_format, format))}
            >
              {copy(VISIT_KEYS[format])}
            </TeamCheckbox>
          )),
        );
      case 'experience':
        return group(
          kind,
          options.experience.map((years) => (
            <TeamCheckbox
              key={years}
              checked={state.experience.includes(years)}
              onChange={() => set('experience', flip(state.experience, years))}
            >
              {copy('team.experience_min', { years })}
            </TeamCheckbox>
          )),
        );
      case 'gender':
        return group(
          kind,
          options.gender.map((gender) => (
            <TeamCheckbox
              key={gender}
              checked={state.gender.includes(gender)}
              onChange={() => set('gender', flip(state.gender, gender))}
            >
              {copy(gender === 'female' ? 'team.gender_female' : 'team.gender_male')}
            </TeamCheckbox>
          )),
        );
      case 'language':
        return group(
          kind,
          options.language.map((language) => (
            <TeamCheckbox
              key={language}
              checked={state.language.includes(language.toLowerCase())}
              onChange={() => set('language', flip(state.language, language.toLowerCase()))}
            >
              {language}
            </TeamCheckbox>
          )),
        );
    }
  });

  return (
    <aside className="mb-8 flex flex-col gap-5 lg:mb-0" data-team-sidebar>
      {searchable ? (
        <input
          type="search"
          value={state.search}
          onChange={(event) => set('search', event.target.value)}
          aria-label={copy('team.search_label')}
          placeholder={copy('team.search_label')}
          className="h-11 w-full border px-4 text-sm"
          style={FIELD_STYLE}
          data-team-search
        />
      ) : null}
      {kinds.length > 0 ? (
        <>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-10 items-center self-start border px-4 text-sm font-semibold lg:hidden"
            style={FIELD_STYLE}
            data-team-filters-toggle
          >
            {copy('team.filters_toggle')}
            {ticked > 0 ? ` (${ticked})` : ''}
          </button>
          <div
            id={`${id}-panel`}
            className={cn('flex-col gap-6 lg:flex', open ? 'flex' : 'hidden')}
            data-team-filters
          >
            {groups}
          </div>
        </>
      ) : null}
      {teamMultiFilterActive(state) ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <p aria-live="polite" style={MUTED} data-team-results>
            {copy('team.results', { count, total })}
          </p>
          <button
            type="button"
            onClick={() => onChange(NO_TEAM_MULTI_FILTER)}
            className="font-semibold underline"
            data-team-clear
          >
            {copy('team.clear_filters')}
          </button>
        </div>
      ) : null}
    </aside>
  );
}

/** The cards, or "nobody matches" - the same in both layouts. */
function TeamGrid({
  section,
  shown,
  roster,
  link,
}: {
  section: TeamSection;
  shown: readonly StorefrontTeamMember[];
  roster: readonly StorefrontTeamMember[];
  link: TeamLink;
}) {
  const copy = useShopContent();
  if (shown.length === 0) {
    return (
      <p className="py-6 text-sm" style={MUTED} data-team-no-results>
        {copy('team.no_results')}
      </p>
    );
  }
  return (
    <ul
      className={cn('grid grid-cols-1 gap-8', GRID[section.columns] ?? GRID[3])}
      data-team-grid
      data-columns={section.columns}
    >
      {shown.map((member) => (
        <MemberCard key={member.id} section={section} member={member} roster={roster} link={link} />
      ))}
    </ul>
  );
}

/**
 * The sidebar layout's body: its own (multi-choice) state, the column and the
 * grid. A component of its own so the top layout's hooks and markup are not
 * touched by it at all.
 */
function TeamSidebarLayout({
  section,
  all,
  roster,
  kinds,
  options,
  link,
}: {
  section: TeamSection;
  all: readonly StorefrontTeamMember[];
  roster: readonly StorefrontTeamMember[];
  kinds: readonly TeamFilterKind[];
  options: TeamFilterOptions;
  link: TeamLink;
}) {
  const [picked, setPicked] = useState<TeamMultiFilterState>(NO_TEAM_MULTI_FILTER);
  const state = effectiveMultiState(picked, kinds, options, section.show_search);
  const shown = filterTeamMulti(all, state);
  return (
    <div className="lg:grid lg:grid-cols-4 lg:items-start lg:gap-10" data-team-layout="sidebar">
      <TeamSidebar
        kinds={kinds}
        options={options}
        searchable={section.show_search}
        state={state}
        onChange={setPicked}
        count={shown.length}
        total={all.length}
      />
      <div className="min-w-0 lg:col-span-3">
        <TeamGrid section={section} shown={shown} roster={roster} link={link} />
      </div>
    </div>
  );
}

export function TeamView({ section, team, preview, heading, emptyHint, link }: TeamViewProps) {
  const copy = useShopContent();
  const [picked, setPicked] = useState<TeamFilterState>(NO_TEAM_FILTER);

  const all = useMemo(() => shownMembers(section, team), [section, team]);
  // The whole list, not just the people this section shows: a sample
  // person's stand-in is seeded by their place in it, as the preview's is.
  const roster = team?.members ?? all;
  const options = useMemo(() => teamFilterOptions(all), [all]);
  // Only the filters with something to offer: one nobody's facts fill is a
  // row of nothing.
  const kinds = teamFilterKinds(section).filter((kind) => teamFilterHasOptions(kind, options));
  const state = effectiveState(picked, kinds, options, section.show_search);
  const shown = filterTeam(all, state);
  // The original speciality chips, alone and without a search box, keep the
  // exact row they always drew.
  const legacyChips = kinds.length === 1 && kinds[0] === 'speciality' && !section.show_search;
  const toolbar = !legacyChips && (kinds.length > 0 || section.show_search);

  if (all.length === 0) {
    // Still loading, or nobody to show: the live band takes its padding with
    // it, and the customiser explains where the people come from.
    if (!preview || team === null) return <div data-section-empty hidden />;
    return (
      <Container>
        {heading}
        {emptyHint}
      </Container>
    );
  }

  // The sidebar only when it has something to hold: with no search box and no
  // filter on offer it would be an empty quarter of the page, so the grid
  // simply takes the width, as in the top layout.
  if (section.filter_layout === 'sidebar' && (kinds.length > 0 || section.show_search)) {
    return (
      <Container>
        {heading}
        <TeamSidebarLayout
          section={section}
          all={all}
          roster={roster}
          kinds={kinds}
          options={options}
          link={link}
        />
      </Container>
    );
  }

  return (
    <Container>
      {heading}
      {/* One chip is no filter at all, so the row appears from two upward
          (`teamFilterOptions` offers no specialities below that). */}
      {legacyChips ? (
        <div
          role="group"
          aria-label={copy('team.filter_label')}
          className="mb-8 flex flex-wrap gap-2"
          data-team-filter="speciality"
        >
          {[null, ...options.speciality].map((value) => (
            <TeamChip
              key={value ?? '__all'}
              pressed={value === state.speciality}
              onClick={() => setPicked({ ...picked, speciality: value })}
            >
              {value ?? copy('team.filter_all')}
            </TeamChip>
          ))}
        </div>
      ) : null}
      {toolbar ? (
        <TeamToolbar
          kinds={kinds}
          options={options}
          searchable={section.show_search}
          state={state}
          onChange={setPicked}
          count={shown.length}
          total={all.length}
        />
      ) : null}
      <TeamGrid section={section} shown={shown} roster={roster} link={link} />
    </Container>
  );
}
